// Domain-light primitive shared by request-workspace surfaces. Encapsulates
// hover state, the selected/hover background token resolution, and the
// click + Enter/Space keyboard activation handlers that the request
// workspace contract requires for full-row affordances. See
// `apps/web/docs/request-workspace-ui-contract.md`.

import { useState, type KeyboardEvent } from 'react';

const HOVER_BG = 'var(--bg-2)';
const SELECTED_BG = 'var(--accent-1-subtle)';
const TRANSPARENT = 'transparent';

export interface InteractiveRowHandlers {
  role: 'button';
  tabIndex: 0;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export interface UseInteractiveRowOptions {
  selected: boolean;
  onActivate: () => void;
}

export interface UseInteractiveRowResult {
  background: string;
  handlers: InteractiveRowHandlers;
}

export function useInteractiveRow({
  selected,
  onActivate,
}: UseInteractiveRowOptions): UseInteractiveRowResult {
  const [hover, setHover] = useState(false);
  const background = selected ? SELECTED_BG : hover ? HOVER_BG : TRANSPARENT;
  const handlers: InteractiveRowHandlers = {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
  return { background, handlers };
}
